import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useUpdateAtom } from 'jotai/utils';

import {
  is_ended_preorder_atom,
  PdpShippingFeeType,
  useCatalogProductIdParams,
  usePdpBaseInfo,
} from '@domains/pdp-common';

import { useCountdownTimer } from '../hooks/useCountdownTimer';

interface GetCountdownTextProps {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isEnded: boolean;
}

function getCountdownText({ days, hours, minutes, seconds, isEnded }: GetCountdownTextProps) {
  if (isEnded) {
    return '0초';
  }

  return days > 0 ? `${days}일` : `${hours}시간 ${minutes}분 ${seconds}초`;
}

export const useShippingPreOrderService = () => {
  const setIsEndedPreorder = useUpdateAtom(is_ended_preorder_atom);
  const [open, setOpen] = useState(false);

  const params = useCatalogProductIdParams();
  const { product } = usePdpBaseInfo(params);

  const { preorder } = product;
  const {
    countdown: { days, hours, minutes, seconds },
    is_ended: is_ended_countdown,
  } = useCountdownTimer(preorder?.end_at);
  const estimated_shipping_date = useMemo(() => {
    if (product.estimated_shipping_date) {
      return product.estimated_shipping_date;
    }
    return {
      estimate_list: null,
      repr_estimate: null,
    };
  }, [product]);

  const start_date = useMemo(() => {
    return dayjs(preorder?.start_at).format('YYYY. M. D(ddd) hh:mm');
  }, [preorder?.start_at]);
  const shipping_details = useMemo(() => {
    return {
      is_shipping_fee_free: product.shipping_fee?.fee_type === PdpShippingFeeType.FREE,
      shipping_fee: product.shipping_fee?.base_fee ?? 0,
      shipping_fee_type: product.shipping_fee?.fee_type ?? PdpShippingFeeType.CHARGED,
      free_shipping_amount: product.shipping_fee?.minimum_free_shipping_fee ?? null,
      shipping_company: product.shipping_company?.base_company ?? null,
      additional_shipping_fee_text: product.shipping_fee?.additional_shipping_fee_text ?? null,
    };
  }, [product.shipping_fee?.fee_type]);

  const is_before = useMemo(() => {
    return dayjs().isBefore(dayjs(preorder?.start_at));
  }, [preorder?.start_at]);

  const isEnded = useMemo(() => {
    if (is_ended_countdown) {
      return true;
    }

    return dayjs().isAfter(dayjs(preorder?.end_at));
  }, [preorder?.end_at, is_ended_countdown]);

  const countdown_text = getCountdownText({ days, hours, minutes, seconds, isEnded });

  const shipping_text = useMemo(() => {
    return dayjs(preorder?.shipping_at).format('M.DD(ddd)');
  }, [preorder]);
  const end_at_text = useMemo(() => {
    return dayjs(preorder?.end_at).format('YYYY.MM.DD(ddd) hh:mm');
  }, [preorder]);

  useEffect(() => {
    if (isEnded) {
      setIsEndedPreorder(isEnded);
    }
  }, [isEnded, setIsEndedPreorder]);

  return {
    open,
    setOpen,
    shipping_details,
    shipping_text,
    is_before,
    start_date,
    isEnded,
    countdown_text,
    end_at_text,
    estimated_shipping_date,
  };
};
